import {
  computed,
  hasInjectionContext,
  inject,
  provide,
  shallowRef,
  toValue,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter,
  type Ref,
  type ShallowRef,
} from 'vue';

/** What one settings key holds: a JSON object. */
export type NxSettingsDocument = Record<string, unknown>;

/**
 * Where kit components read and write the user's settings („Einstellungen",
 * I-2510). The kit only defines the contract; the app shell implements it —
 * Connect backs it with the account config path (`/v1/account/configs`,
 * `useAccountConfig`) and provides it app-wide under `nxSettingsKey`. The kit
 * never imports the shell.
 *
 * A key holds a JSON object. `patch` is a JSON merge patch (RFC 7386): objects
 * merge member by member, `null` removes a member, arrays and scalars replace.
 * A list the user reorders or toggles item by item is therefore a map by id.
 *
 * Without a provider (the kit on its own, tests, harnesses) every component
 * keeps what it would store in memory for its own lifetime — see
 * `useNxSettings`.
 */
export interface NxSettingsSource {
  /** The effective document of a key; `undefined` while nothing is known of it. */
  get<T extends object = NxSettingsDocument>(key: string): Readonly<Ref<T | undefined>>;
  /** Writes a merge patch onto the key; `get` shows the result at once. */
  patch(key: string, partial: NxSettingsDocument): void;
  /**
   * The top-level fields of a key an earlier layer locked (a tenant or a
   * company): they cannot be changed here — a write of one is refused. A
   * source without locks leaves it out.
   */
  locked?(key: string): Readonly<Ref<readonly string[]>>;
}

export const nxSettingsKey: InjectionKey<NxSettingsSource> = Symbol('nx-settings');

/**
 * Provides a settings source to the calling component's subtree — a preview
 * that shows other settings than the shell, say. The app-wide source is an
 * app-level provide (`app.provide(nxSettingsKey, source)`).
 */
export function provideNxSettings(source: NxSettingsSource): void {
  provide(nxSettingsKey, source);
}

/**
 * The settings source of the calling component: the provided one, else a
 * memory source of its own (so what it changes lasts as long as it does).
 */
export function useNxSettings(): NxSettingsSource {
  const provided = hasInjectionContext() ? inject(nxSettingsKey, null) : null;
  return provided ?? createNxMemorySettings();
}

function isDocument(value: unknown): value is NxSettingsDocument {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Applies a JSON merge patch (RFC 7386) to a document: objects merge member by
 * member, `null` removes a member, anything else replaces. Neither argument is
 * modified.
 */
export function applyNxMergePatch(target: unknown, patch: unknown): unknown {
  if (!isDocument(patch)) return patch;
  const result: NxSettingsDocument = isDocument(target) ? { ...target } : {};
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if (value === null) delete result[key];
    else result[key] = applyNxMergePatch(result[key], value);
  }
  return result;
}

/**
 * A settings source kept in memory: `initial` seeds keys, `patch` applies
 * merge patches. What `useNxSettings` falls back to without a provider, and
 * what a preview or a test provides.
 */
export function createNxMemorySettings(
  initial: Record<string, NxSettingsDocument> = {}
): NxSettingsSource {
  const docs = new Map<string, ShallowRef<NxSettingsDocument | undefined>>();
  const docOf = (key: string): ShallowRef<NxSettingsDocument | undefined> => {
    let doc = docs.get(key);
    if (!doc) {
      doc = shallowRef(initial[key]);
      docs.set(key, doc);
    }
    return doc;
  };
  return {
    get: <T extends object>(key: string) => docOf(key) as unknown as Readonly<Ref<T | undefined>>,
    patch(key, partial) {
      const doc = docOf(key);
      const next = applyNxMergePatch(doc.value ?? {}, partial);
      doc.value = isDocument(next) ? next : {};
    },
  };
}

/** The settings key of the shell's own UI state. */
export const NX_SHELL_SETTINGS_KEY = 'shell';

/**
 * The remembered UI state of one kit component — the open regions and widths
 * of an NxWorkspace, a folded NxSidebarWidget, where the user dragged an
 * NxFab. Every such component keeps an object of its own under its storage
 * key in the `panes` map of the shell's settings (`shell.panes.<storageKey>`).
 * An empty storage key remembers nothing (and writes nothing).
 */
export function useNxPaneState<T extends object>(
  storageKey: MaybeRefOrGetter<string | null | undefined>,
  source: NxSettingsSource = useNxSettings()
): {
  /** What is stored for the component (`{}` when nothing is). */
  state: ComputedRef<Partial<T>>;
  /** Merges a patch into the component's stored object (`null` removes a member). */
  patch: (partial: { [K in keyof T]?: T[K] | null }) => void;
} {
  const shell = source.get<{ panes?: unknown }>(NX_SHELL_SETTINGS_KEY);
  const state = computed<Partial<T>>(() => {
    const key = toValue(storageKey);
    const panes = shell.value?.panes;
    if (!key || !isDocument(panes)) return {};
    const entry = panes[key];
    return isDocument(entry) ? (entry as Partial<T>) : {};
  });
  function patch(partial: { [K in keyof T]?: T[K] | null }): void {
    const key = toValue(storageKey);
    if (!key) return;
    source.patch(NX_SHELL_SETTINGS_KEY, { panes: { [key]: partial } });
  }
  return { state, patch };
}
